import React, {useCallback, useEffect, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from 'lodash/noop';
import IssuePreviewHeader from './IssuePreviewHeader';
import IssuePreviewSelectedTab from './IssuePreviewSelectedTab';
import IssuePreviewNoTypeStatusChangePopup from './IssuePreviewNoTypeStatusChangePopup';
import Loader from '@adsk/bim360-matrix-react-loader';
import StickyScrollableContainer from '@adsk/bim360-matrix-react-sticky-scrollable-container';
import Tabs from '@adsk/bim360-matrix-react-tabs';
import AnimateHeightPopUp from '../common/AnimateHeightPopUp';
import i18n from '../../utils/i18n';
import {
  documentCollectionType,
  documentType,
  idToTitleMapType,
  issueAttributesType,
  oxygenType,
  pendingIssueFormAttributesType,
  statusOptionsArrayType
} from '../../constants/proptype-validation';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {screenType} from '../../constants/analytics';
import {STATUS_CLOSED, STATUS_VOID} from '../../constants/status-types';
import {entityPreview, getAssigneeTypeByAssigneeId} from '../../utils/analytics-helpers';
import {PreviewNavigationTabIds} from '../../constants/navigation';
import {getUrn, hasPermittedAttribute} from '../../utils/issues-helpers';
import useCurrentIssuePreviewPopup, {IssuePreviewPopupTypes} from './useCurrentIssuePreviewPopup';
import IssuePreviewTransitionPopUp from './IssuePreviewTransitionPopUp';
import IssuePreviewUnlinkConfirmationPopUp from './IssuePreviewUnlinkConfirmationPopUp';
import {clearAllThumbnails, fetchActivities, trackEvent, updateIssueAction} from '../../app/actions';
import {
  getCurrentDocument,
  getCurrentIssuePreviewStatusOptions,
  getCurrentPreviewIssueAttributes,
  getCurrentPreviewIssueWrapper,
  getCurrentUserId,
  getDocumentCollection,
  getFetchingIssuesUrns,
  getFieldIssueMandatoryDataLoading,
  getFieldIssueTypesAndSubTypesIdToTitleMap,
  getOxygenMappings,
  getIsLoadingCurrentUserPermissions
} from '../../app/selectors';
import {connect} from '../../app/connect';
import {ActivityBatchReadTypes} from '../../constants/activity';
import {useRefetchIssueOnChangeFromIframe} from '../../utils/docsIframeMessage-helper';
import useContentElementRef from './useContentElementRef';

export const PreviewNavigationTabs = [
  {
    id: PreviewNavigationTabIds.DETAILS,
    name: () => i18n.t('preview.tabs.details')
  },
  {
    id: PreviewNavigationTabIds.ATTACHMENTS,
    name: () => i18n.t('preview.tabs.attachments')
  },
  {
    id: PreviewNavigationTabIds.ACTIVITY,
    name: () => i18n.t('preview.tabs.activity')
  }
];

const useTrackEntityPreviewEvent = ({
  issue,
  currentUserId,
  isFetchingCurrentDocument,
  isLoadingCurrentUserPermissions,
  oxygen,
  origin,
  documentCollection,
  document,
  issueTypesAndSubTypesTitleMap,
  dispatch
}) => {
  useEffect(() => {
    if (issue && currentUserId && !isFetchingCurrentDocument && !isLoadingCurrentUserPermissions) {
      const {
        [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: currentAssigneeId,
        [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]: currentAssignedToType
      } = issue;
      const assigneeType = getAssigneeTypeByAssigneeId(currentAssigneeId, currentUserId, oxygen, currentAssignedToType);
      const currentDocument =
        origin === screenType.PROJECT && getUrn(issue) && documentCollection
          ? documentCollection[getUrn(issue)]
          : document
            ? document
            : null;
      const issueTypeTitle = issueTypesAndSubTypesTitleMap[issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]];
      const issueSubTypeTitle = issueTypesAndSubTypesTitleMap[issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]];

      //MixPanel Event - show issue when issue get open
      dispatch(
        trackEvent(
          entityPreview(currentDocument, issue.status, origin, assigneeType, issueTypeTitle, issueSubTypeTitle)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUserId,
    document,
    documentCollection,
    isFetchingCurrentDocument,
    isLoadingCurrentUserPermissions,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    issue?.id,
    issueTypesAndSubTypesTitleMap,
    origin,
    oxygen,
    dispatch
  ]);
};

const IssuePreview = ({
  container,
  loading,
  issue,
  oxygen,
  disableKeyboard,
  origin,
  document,
  currentUserId,
  isLoadingCurrentUserPermissions,
  documentCollection,
  issueStatusOptions,
  issueTypesAndSubTypesTitleMap,
  isFetchingCurrentDocument,
  dispatch
}) => {
  const [selectedTabId, setSelectedTabId] = useState(PreviewNavigationTabIds.DETAILS);
  const [changeToStatus, setChangeToStatus] = useState(null);
  const {currentIssuePreviewPopup, setCurrentIssuePreviewPopup} = useCurrentIssuePreviewPopup();
  useTrackEntityPreviewEvent({
    issue,
    currentUserId,
    isFetchingCurrentDocument,
    isLoadingCurrentUserPermissions,
    oxygen,
    origin,
    documentCollection,
    document,
    issueTypesAndSubTypesTitleMap,
    dispatch
  });

  useEffect(
    () => () => {
      dispatch(clearAllThumbnails());
    },
    [dispatch]
  );

  useEffect(() => {
    if (currentIssuePreviewPopup === null) {
      setChangeToStatus(null);
    }
  }, [currentIssuePreviewPopup]);

  // This is required for action button conatiner reference
  const {contentElementRef, setContentElementRef} = useContentElementRef(null);

  useRefetchIssueOnChangeFromIframe(issue, dispatch);

  const handleOnStatusChange = useCallback(newStatus => {
    if (!newStatus) {
      return;
    }
    if (issue.status === STATUS_CLOSED || issue.status === STATUS_VOID) {
      setChangeToStatus(newStatus);
      dispatch(
        updateIssueAction(
          issue,
          {
            [ISSUE_ATTRIBUTE_NAMES.STATUS]: newStatus.value
          },
          {
            onError: () => {
              setChangeToStatus(null);
            },
            onSuccess: updatedIssue => {
              setChangeToStatus(null);
              if (selectedTabId === PreviewNavigationTabIds.ACTIVITY) {
                const updateTimestamp = new Date(updatedIssue.updatedAt).getTime();
                dispatch(
                  fetchActivities({
                    issueId: issue.id,
                    readType: ActivityBatchReadTypes.LATEST,
                    triggerId: updateTimestamp,
                    triggerTimestamp: updateTimestamp
                  })
                );
              }
            }
          }
        )
      );
    } else {
      setChangeToStatus(newStatus);
      setCurrentIssuePreviewPopup(IssuePreviewPopupTypes.TRANSITION);
    }
  }, [issue, dispatch, selectedTabId, setCurrentIssuePreviewPopup]);

  const currentPopup = useMemo(() => {
    switch (currentIssuePreviewPopup) {
      case IssuePreviewPopupTypes.TRANSITION:
        return (
          <IssuePreviewTransitionPopUp
            isOpen={currentIssuePreviewPopup === IssuePreviewPopupTypes.TRANSITION}
            changeToStatus={changeToStatus ? changeToStatus.value : null}
            onStatusChange={handleOnStatusChange}
          />
        );
      case IssuePreviewPopupTypes.NO_TYPE_STATUS_CHANGE:
        return <IssuePreviewNoTypeStatusChangePopup issue={issue} />;
      case IssuePreviewPopupTypes.CONFIRM_UNLINK:
        return <IssuePreviewUnlinkConfirmationPopUp issue={issue} />;
      default:
        return null;
    }
  }, [changeToStatus, currentIssuePreviewPopup, handleOnStatusChange, issue]);

  if (loading || !issue) {
    return <Loader />;
  }

  return (
    <StickyScrollableContainer className="IssuePreview" ref={setContentElementRef}>
      <StickyScrollableContainer.Header>
        <IssuePreviewHeader
          issue={issue}
          enableStatusChange={hasPermittedAttribute(issue, ISSUE_ATTRIBUTE_NAMES.STATUS) && !changeToStatus}
          changeToStatus={changeToStatus}
          issueStatusOptions={issueStatusOptions}
          onStatusChange={handleOnStatusChange}
          container={container}
        />
        <AnimateHeightPopUp isOpen={currentIssuePreviewPopup !== null}>{currentPopup}</AnimateHeightPopUp>
        <Tabs
          className="IssuePreview__navigation-tabs"
          tabs={PreviewNavigationTabs}
          selectedTab={PreviewNavigationTabs.find(nav => selectedTabId === nav.id)}
          onTabChange={tab => {
            setSelectedTabId(tab.id);
          }}
        />
      </StickyScrollableContainer.Header>
      <StickyScrollableContainer.Content
        className={classNames('IssuePreview__content', {
          'IssuePreview__content--shadowed': currentIssuePreviewPopup !== null
        })}
      >
        <IssuePreviewSelectedTab
          container={container}
          issue={issue}
          selectedTabId={selectedTabId}
          disableKeyboard={disableKeyboard}
          contentElementRef={contentElementRef}
        />
      </StickyScrollableContainer.Content>
    </StickyScrollableContainer>
  );
};

IssuePreview.displayName = 'IssuePreviewModal';

IssuePreview.defaultProps = {
  disableKeyboard: noop
};

IssuePreview.propTypes = {
  container: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  issue: PropTypes.oneOfType([issueAttributesType, pendingIssueFormAttributesType]),
  oxygen: oxygenType.isRequired,
  disableKeyboard: PropTypes.func,
  origin: PropTypes.string,
  document: documentType,
  currentUserId: PropTypes.string.isRequired,
  isLoadingCurrentUserPermissions: PropTypes.bool.isRequired,
  documentCollection: documentCollectionType,
  issueStatusOptions: statusOptionsArrayType.isRequired,
  issueTypesAndSubTypesTitleMap: idToTitleMapType.isRequired,
  isFetchingCurrentDocument: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const {loading = true} = getCurrentPreviewIssueWrapper(state);
  const issue = getCurrentPreviewIssueAttributes(state);
  const oxygen = getOxygenMappings(state);
  const currentUserId = getCurrentUserId(state);
  const document = getCurrentDocument(state);
  const documentCollection = getDocumentCollection(state);
  const fetchingIssuesUrns = getFetchingIssuesUrns(state);
  const issueStatusOptions = getCurrentIssuePreviewStatusOptions(state);

  return {
    issue,
    loading: loading || getFieldIssueMandatoryDataLoading(state),
    currentUserId,
    isLoadingCurrentUserPermissions: getIsLoadingCurrentUserPermissions(state),
    oxygen,
    document,
    documentCollection,
    issueStatusOptions,
    issueTypesAndSubTypesTitleMap: getFieldIssueTypesAndSubTypesIdToTitleMap(state),
    isFetchingCurrentDocument: !!getUrn(issue) && !!fetchingIssuesUrns[getUrn(issue)]
  };
};

export default connect(mapStateToProps)(IssuePreview);
